import { SolanaAddress } from "@shared/utils/address";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    ArrowShortLeftIcon,
    RetryIcon,
    TooltipIcon
} from "@solomon-labs/design/components/icons";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import {
    Button,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";

import { readPendingEnrollment } from "@/hooks/yaas/pendingEnrollmentVerification";
import { useRetailTerms } from "@/hooks/yaas/useRetailTerms";
import { useSquadsDestinationVerification } from "@/hooks/yaas/useSquadsDestinationVerification";
import { useEnrollmentFormAnalytics } from "@/services/analytics/useEnrollmentFormAnalytics";

import { DestinationCheckNotice } from "../application/DestinationCheckNotice";
import { DistributionModeField } from "../application/DistributionModeField";
import { EnableTosCheckbox } from "../application/EnableTosCheckbox";
import { LabeledRewardsDestination } from "../application/LabeledRewardsDestination";
import { TermsLoadNotice } from "../application/TermsLoadNotice";
import type { DistributionMode } from "../application/types";
import { enrollmentRetryOptions } from "../flow/retailEnrollmentFailure";
import type { UseYaasRetailFlowResult } from "../flow/useYaasRetailFlow";

export interface EnableRewardsFormProps {
    flow: Pick<
        UseYaasRetailFlowResult,
        | "analytics"
        | "isSubmittingApplication"
        | "formPhase"
        | "formFailure"
        | "termsResetKey"
        | "verificationMethod"
        | "signingFallback"
        | "submitApplication"
        | "cancelEnrollment"
        | "clearFailure"
    >;
    /** Initial rewards destination, prefilled from the connected wallet. */
    connectedWallet: string;
    signerAddress?: string;
    /** A live eligibility check can block initial submission and verification retries. */
    eligibilityNotice?: string | null;
}

/**
 * Enable Rewards (Mode 2) enrollment form. Opened from the Start
 * Earning CTA on the projection card. Self-contained — owns its own
 * form-field state, address verification, and submit gating. Parent
 * only toggles visibility and supplies the connected wallet (used as
 * the rewards-destination prefill per the intent doc).
 */
export function EnableRewardsForm({
    flow,
    connectedWallet,
    signerAddress = connectedWallet,
    eligibilityNotice = null
}: EnableRewardsFormProps): React.ReactElement {
    const published = useRetailTerms(flow.analytics);
    const [mode, setMode] = useState<DistributionMode>("stream");
    const [label, setLabel] = useState("");
    const [addressInput, setAddress] = useState(connectedWallet);
    // Flag off: the field is not rendered, so read the wallet live rather
    // than the prefill, which would go stale on a wallet switch.
    const destinationOffered = FEATURE_FLAGS.retailEnrollmentDestination;
    const address = destinationOffered ? addressInput : connectedWallet;
    const [retryDeadline, setRetryDeadline] = useState<number | null>(null);
    const [clockMs, setClockMs] = useState(() => Date.now());
    // Backend rejects `tosAccepted !== true` before signature verify
    // (canonicalizeEnrollmentPayload). User must opt in explicitly each
    // time the form opens — never persisted, never default-true.
    const [tosAccepted, setTosAccepted] = useState(false);
    const trimmedAddress = address.trim();
    // A Squads vault is enrolled by a member wallet signing for it, so the
    // copy names the multisig and the signer rather than wallet ownership.
    const forMultisig = signerAddress !== connectedWallet;
    // Only a destination someone typed can be a Squads vault, so with no
    // field the preflight neither runs nor gates the submit.
    const squadsVerification = useSquadsDestinationVerification(
        address,
        signerAddress,
        flow.analytics,
        destinationOffered,
        signerAddress !== connectedWallet || FEATURE_FLAGS.retailSquadsMultisig
    );
    // Empty rewards destination is permitted (backend treats it as
    // "use connected wallet"); a non-empty value must pass the base58
    // shape check before we let the user submit.
    const addressOk =
        trimmedAddress === "" || SolanaAddress.isShape(trimmedAddress);
    const inFlight =
        flow.isSubmittingApplication ||
        flow.formPhase === "verifying" ||
        flow.formPhase === "verified" ||
        flow.formPhase === "submitting";
    const retrySecondsLeft =
        flow.formFailure?.code === "rate_limited" && retryDeadline !== null
            ? Math.max(0, Math.ceil((retryDeadline - clockMs) / 1000))
            : 0;
    const retryBlocked = retrySecondsLeft > 0;
    const submitDisabled =
        eligibilityNotice !== null ||
        !published.terms ||
        !tosAccepted ||
        !addressOk ||
        !squadsVerification.canSubmit ||
        inFlight ||
        retryBlocked;

    const analytics = useEnrollmentFormAnalytics(flow.analytics, {
        mode,
        address,
        connectedWallet,
        label,
        addressValid: addressOk,
        termsVersion: published.terms?.version,
        termsError: !!published.error,
        termsLoading: published.isLoading,
        termsAccepted: tosAccepted,
        destinationState:
            squadsVerification.status.status === "success"
                ? squadsVerification.status.signerStatus
                : squadsVerification.status.status,
        destinationAllowed: squadsVerification.canSubmit,
        destinationFailed: squadsVerification.status.status === "lookup-failed",
        inFlight,
        hasPendingProof:
            !!readPendingEnrollment(connectedWallet)?.transactionSignature
    });

    // Error-path CTA: on failure the button reads "Verification Failed" with
    // the light-bar error flash, then offers the relevant retry after a beat.
    // The explanation stays visible so the user knows why a transfer is offered.
    const isError = flow.formFailure !== null;
    const retryOptions = enrollmentRetryOptions(flow.formFailure);
    const transferIsPrimary = retryOptions.preferredMethod === "microTransfer";
    // How long the failure stays on the button before it turns into the way
    // out. Long enough to read the words and register that the attempt ended,
    // rather than seeing the label change out from under the cursor.
    const RETRY_DELAY_MS = 3000;
    // The label crossfades rather than swapping: the old words leave before
    // the new ones arrive, so the change reads as one gesture instead of a
    // flicker. `mode="wait"` sequences them, so the swap costs twice this.
    const LABEL_FADE_S = 0.4;
    // A declined transfer is the user's answer, not an error to explain or
    // retry: the button says so for the same beat, then the form is back at
    // its start.
    const canceled = flow.formFailure?.canceled === true;
    const [showRetry, setShowRetry] = useState(false);
    const { clearFailure } = flow;
    useEffect(() => {
        if (!isError) {
            setShowRetry(false);
            return;
        }
        setShowRetry(false);
        const timer = window.setTimeout(
            () => (canceled ? clearFailure() : setShowRetry(true)),
            RETRY_DELAY_MS
        );
        return (): void => window.clearTimeout(timer);
    }, [isError, canceled, clearFailure]);

    useEffect(() => {
        if (
            flow.formFailure?.code !== "rate_limited" ||
            flow.formFailure.retryAfterSeconds === null
        ) {
            setRetryDeadline(null);
            return;
        }
        setRetryDeadline(
            Date.now() + flow.formFailure.retryAfterSeconds * 1000
        );
        setClockMs(Date.now());
        const timer = window.setInterval(() => setClockMs(Date.now()), 1000);
        return (): void => window.clearInterval(timer);
    }, [
        flow.formFailure?.code,
        flow.formFailure?.requestId,
        flow.formFailure?.retryAfterSeconds
    ]);

    useEffect(() => {
        if (flow.formFailure?.code !== "terms_revision_mismatch") return;
        setTosAccepted(false);
    }, [flow.formFailure?.code, flow.termsResetKey]);

    useEffect(() => {
        setTosAccepted(false);
    }, [published.terms?.contentDigest, connectedWallet]);
    // `termsResetKey` is the trigger: the ref makes each key fire at most
    // once, so a new `refresh` or failure code never refetches on its own.
    const failureCode = flow.formFailure?.code;
    const { refresh: refreshTerms } = published;
    const handledTermsResetKey = useRef<number | null>(null);
    useEffect(() => {
        if (handledTermsResetKey.current === flow.termsResetKey) return;
        handledTermsResetKey.current = flow.termsResetKey;
        if (failureCode === "terms_revision_mismatch") void refreshTerms();
    }, [flow.termsResetKey, failureCode, refreshTerms]);

    // Switching to a transfer is a different action, not another go at the
    // same one, so it is the one retry branch without the retry icon.
    const offersTransferInstead =
        showRetry &&
        !flow.formFailure?.retryWithNewChallenge &&
        !flow.formFailure?.hasTransferProof &&
        transferIsPrimary;
    // Signing fell through to a transfer: the button stays pending and says
    // so, and the why sits one hover away instead of in a line above it.
    // It holds until the wallet has sent the transfer, rather than stepping
    // through "Approve small transfer…" underneath it.
    const showsSigningFallback =
        !isError && flow.formPhase === "verifying" && flow.signingFallback;
    // Proof in hand, server still checking it: the signature, or the sent
    // transfer, which the server reads onchain now. Pending either way;
    // "Ownership Verified" waits for the server to accept it.
    const proofPhase =
        !isError &&
        (flow.formPhase === "verified" || flow.formPhase === "submitting");
    const verifyingTransfer =
        proofPhase && flow.verificationMethod === "microTransfer";
    const verifyingSignature =
        proofPhase && flow.verificationMethod === "signMessage";
    const ownershipVerified = !isError && flow.formPhase === "submitted";
    const buttonLabel = isError
        ? showRetry
            ? retryBlocked
                ? `Retry Verification in ${Math.floor(retrySecondsLeft / 60)}:${String(retrySecondsLeft % 60).padStart(2, "0")}`
                : flow.formFailure?.retryWithNewChallenge
                  ? // Same words as the default retry: both are the manual
                    // way out. The difference — that a new challenge can
                    // require another transfer and network fee — is carried
                    // by `formFailure.displayMessage` above the button.
                    "Retry Verification"
                  : flow.formFailure?.hasTransferProof
                    ? "Check verification again"
                    : offersTransferInstead
                      ? "Verify with a small transfer"
                      : "Retry Verification"
            : canceled
              ? "Verification Cancelled"
              : "Verification Failed"
        : ownershipVerified
          ? forMultisig
              ? "Signer Verified"
              : "Ownership Verified"
          : verifyingTransfer
            ? "Verifying Transfer…"
            : verifyingSignature
              ? "Verifying Signature…"
              : showsSigningFallback
                ? "Signing Not Available, Trying Transfer…"
                : flow.formPhase === "verifying"
                  ? flow.verificationMethod === "microTransfer"
                      ? "Approve small transfer…"
                      : "Sign Verification Message…"
                  : flow.formPhase === "submitting"
                    ? "Submitting…"
                    : forMultisig
                      ? "Verify Signer to Start Earning"
                      : "Verify Ownership to Start Earning";

    const submit = (preferredMethod?: "microTransfer"): void => {
        if (submitDisabled) return;
        const draft = {
            distributionMode: mode,
            rewardsDestination: trimmedAddress,
            accountLabel: label,
            tosAccepted,
            terms: published.terms ?? undefined
        };
        const method = preferredMethod ?? retryOptions.preferredMethod;
        if (isError)
            flow.analytics?.send("enroll:retry_clicked", {
                retry_target: flow.formFailure?.hasTransferProof
                    ? "submission"
                    : "verification",
                retry_action: retryOptions.restartVerification
                    ? "new_challenge"
                    : flow.formFailure?.hasTransferProof
                      ? "reuse_proof"
                      : "new_challenge"
            });
        if (method === "microTransfer" && !flow.formFailure?.hasTransferProof)
            flow.analytics?.send("enroll:verification_method_selected", {
                verification_method: "micro_transfer",
                reason: "explicit_fallback"
            });
        if (retryOptions.restartVerification) {
            void flow.submitApplication(draft, method, true);
        } else if (method) {
            void flow.submitApplication(draft, method);
        } else {
            void flow.submitApplication(draft);
        }
    };

    return (
        <motion.div
            key="enable-rewards"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
            className="flex flex-col gap-3"
        >
            <Button
                variant="link"
                size="sm"
                onClick={(): void => {
                    flow.analytics?.close("back", {
                        dirty:
                            mode !== "stream" ||
                            address !== connectedWallet ||
                            !!label ||
                            tosAccepted
                    });
                    flow.cancelEnrollment();
                }}
                className="self-start"
            >
                <ArrowShortLeftIcon className="size-4" />
                Back
            </Button>
            <SectionHeading variant="h1">Enable Rewards</SectionHeading>
            {eligibilityNotice && (
                <Text variant="small" role="status">
                    {eligibilityNotice}
                </Text>
            )}
            {/* Flag off: no choice is offered and `mode` stays at its
                "stream" default, so enrollment always submits Stream. */}
            {FEATURE_FLAGS.retailDistributionModeChoice && (
                <div className="mt-3">
                    <DistributionModeField
                        value={mode}
                        onChange={(next): void => {
                            if (next !== mode)
                                flow.analytics?.send(
                                    "enroll:distribution_selected",
                                    {
                                        previous_mode: mode,
                                        distribution_mode: next
                                    }
                                );
                            setMode(next);
                        }}
                        streamLabel="Stream"
                        claimLabel="Claim"
                        streamDescription="Rewards will stream directly to your address"
                        claimDescription="Claim rewards whenever you want"
                    />
                </div>
            )}
            {/* With the flag off the destination group is not offered: the
                address state stays at its connected-wallet prefill, so the
                submit still names the connected wallet, and a line in the
                group's place says so, since the form otherwise never names
                where rewards go. The Squads notice goes with the group:
                its preflight is off too (see `destinationOffered`), so
                nothing it could say would gate the submit. */}
            {destinationOffered ? (
                <div
                    className="mt-6 flex flex-col gap-2"
                    onBlur={analytics.commitDestination}
                >
                    {/* Squads preflight replaces the generic account hint while
                        resolving or when it has a multisig-specific result. */}
                    <LabeledRewardsDestination
                        label={label}
                        onLabelChange={setLabel}
                        address={address}
                        onAddressChange={setAddress}
                        connectedWallet={connectedWallet}
                        labelPlaceholder="Destination Nickname (Optional)"
                        hideOnChainStatus={false}
                        verificationContent={
                            <DestinationCheckNotice
                                message={squadsVerification.message}
                                isError={squadsVerification.isError}
                                canRetry={squadsVerification.canRetry}
                                onRetry={(): void => {
                                    analytics.retryCheck("destination");
                                    squadsVerification.retry();
                                }}
                                multisig={
                                    squadsVerification.status.status ===
                                    "success"
                                        ? squadsVerification.status.multisig
                                        : null
                                }
                            />
                        }
                    />
                </div>
            ) : (
                // The margin separates the line from the distribution
                // choice; with that flagged off too, it follows the
                // heading on the form's own gap.
                <Text
                    variant="small"
                    render={<p />}
                    className={
                        FEATURE_FLAGS.retailDistributionModeChoice
                            ? "mt-6 text-foreground-muted"
                            : "text-foreground-muted"
                    }
                >
                    {forMultisig
                        ? "Rewards will be sent to the multisig."
                        : "Rewards will be sent to the same wallet you enroll with."}
                </Text>
            )}
            {/* With no document there is nothing to agree to, so the
                notice takes the checkbox's place rather than stacking
                above it. */}
            {published.terms ? (
                <EnableTosCheckbox
                    analytics={flow.analytics}
                    forMultisig={forMultisig}
                    terms={published.terms}
                    checked={tosAccepted}
                    onCheckedChange={setTosAccepted}
                    disabled={inFlight}
                    reopenKey={flow.termsResetKey}
                />
            ) : (
                <TermsLoadNotice
                    error={published.error}
                    onRefresh={(): void => {
                        analytics.retryCheck("terms");
                        void published.refresh();
                    }}
                />
            )}
            {flow.formFailure && !canceled && (
                <>
                    <Text
                        variant="small"
                        render={<p role="alert" />}
                        className="text-foreground-muted"
                    >
                        {flow.formFailure.displayMessage}
                    </Text>
                    {flow.formFailure.requestId && (
                        <Text
                            variant="fine-print"
                            render={<p />}
                            className="text-foreground-muted"
                        >
                            Support ID: {flow.formFailure.requestId}
                        </Text>
                    )}
                </>
            )}
            <Button
                variant="raised"
                interaction="light-bar"
                size="lg"
                fullWidth
                className="mt-2"
                // Waiting on the wallet or the server is a pending state,
                // same as a swap in flight. `inFlight` covers verifying,
                // verified and submitting, which are exactly the three
                // labels that describe work already underway.
                status={
                    isError && !showRetry
                        ? "error"
                        : ownershipVerified
                          ? "success"
                          : inFlight
                            ? "pending"
                            : undefined
                }
                disabled={
                    submitDisabled ||
                    (isError && !showRetry) ||
                    flow.formPhase === "submitted"
                }
                onClick={(): void =>
                    submit(transferIsPrimary ? "microTransfer" : undefined)
                }
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={buttonLabel}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: LABEL_FADE_S,
                            ease: "easeOut"
                        }}
                        // gap-3: the retry glyph sat too tight against the
                        // label at the button's own gap-2.
                        className="flex items-center justify-center gap-3"
                    >
                        {isError && showRetry && !offersTransferInstead && (
                            <RetryIcon className="size-5" />
                        )}
                        {showsSigningFallback ? (
                            <span className="flex items-center gap-2">
                                {buttonLabel}
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger
                                            // The button is disabled while
                                            // pending, and a disabled Button
                                            // sets pointer-events-none, which
                                            // this span would inherit and
                                            // never open on hover.
                                            render={
                                                <span
                                                    aria-label="Why a transfer"
                                                    className="pointer-events-auto"
                                                />
                                            }
                                        >
                                            <TooltipIcon className="size-4" />
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="top"
                                            maxWidth="sm"
                                        >
                                            Unable to sign a transaction with
                                            your wallet. You can verify with a
                                            tiny transfer instead.
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </span>
                        ) : (
                            buttonLabel
                        )}
                    </motion.span>
                </AnimatePresence>
            </Button>
            {isError &&
                showRetry &&
                !transferIsPrimary &&
                flow.formFailure?.canRetryWithMicroTransfer && (
                    <Button
                        variant="link"
                        size="sm"
                        disabled={submitDisabled}
                        onClick={(): void => submit("microTransfer")}
                        className="self-start"
                    >
                        Verify with a small transfer instead
                    </Button>
                )}
        </motion.div>
    );
}
